import React from "react";
import { Link } from "react-router-dom";

const Section = ({ n, title, children }) => (
  <section className="mt-10">
    <h2 className="font-display text-2xl lg:text-3xl">
      <span className="text-accent-foreground bg-accent px-2 py-0.5 mr-3 text-lg font-mono align-middle">{String(n).padStart(2, "0")}</span>
      {title}
    </h2>
    <div className="mt-4 space-y-4 text-[17px] leading-relaxed text-primary/90">
      {children}
    </div>
  </section>
);

export default function Privacy() {
  return (
    <div>
      <section className="bg-primary text-primary-foreground">
        <div className="mx-auto max-w-[900px] px-5 lg:px-8 py-14">
          <div className="label-caps text-accent mb-3">Legal</div>
          <h1 className="font-display text-4xl lg:text-6xl leading-[1.05]">Privacy Policy</h1>
          <p className="mt-6 text-primary-foreground/80">Veterans Disability Education</p>
          <p className="mt-1 text-sm text-primary-foreground/60 font-mono">Effective date: August 1, 2026</p>
        </div>
      </section>

      <article className="mx-auto max-w-[900px] px-5 lg:px-8 py-14">
        <p className="text-lg leading-relaxed text-primary/90">
          Veterans Disability Education ("VDE," "we," "us") respects your privacy. This policy explains what information we collect through veteransdisabilityeducation.com (the "Site"), how we use it, and the choices you have. By using the Site, you agree to this policy.
        </p>

        <Section n={1} title="Information We Collect">
          <p className="font-semibold text-primary">Information you provide directly.</p>
          <ul className="list-disc pl-6 space-y-2">
            <li><strong>Contact and intake information:</strong> name, email address, phone number, state of residence, age range, and the service history you share through our intake form, including branch of service, dates of service, military occupation, vessel names, discharge characterization, and information about your current VA rating, claims history, and goals.</li>
            <li><strong>Documents you upload:</strong> if you create a portal account, you may upload documents such as lay statements, witness statements, medical records, nexus letters, and VA correspondence. These may contain sensitive health information. We collect these only because you choose to provide them for the purpose of receiving education and strategy guidance.</li>
            <li><strong>Account information:</strong> login credentials for your portal account.</li>
            <li><strong>Payment information:</strong> payments are processed by Stripe. We never see or store your full card number. We receive confirmation of your purchase, the package purchased, and your billing name and email.</li>
            <li><strong>Communications:</strong> messages you send us through contact forms or email.</li>
          </ul>
          <p className="font-semibold text-primary pt-2">Information collected automatically.</p>
          <p>Basic technical data such as browser type, device type, and pages visited, used to keep the Site functioning and secure. We keep automatic collection to the minimum needed to operate the Site.</p>
        </Section>

        <Section n={2} title="How We Use Your Information">
          <p>We use your information to:</p>
          <ul className="list-disc pl-6 space-y-2">
            <li>Provide education, coaching, and strategy guidance you request</li>
            <li>Review your intake to determine whether our services are a fit</li>
            <li>Operate your secure portal, including organizing the documents you upload</li>
            <li>Schedule and conduct consultations</li>
            <li>Process payments and send transactional messages such as intake confirmations and password resets</li>
            <li>Send educational content and updates if you opt in, with an unsubscribe link in every message</li>
            <li>Comply with legal obligations and protect against fraud or misuse</li>
          </ul>
          <div className="bg-secondary border-l-4 border-accent p-5 mt-4">
            <p className="font-semibold text-primary">We do not sell your personal information.</p>
            <p className="mt-2">We do not share your information with data brokers. We will never share your medical or service records with anyone outside VDE except at your direction or as required by law.</p>
          </div>
        </Section>

        <Section n={3} title="How Your Information Is Stored and Protected">
          <p>Documents and personal information are stored in our application database with access limited to you and authorized VDE personnel. Files are protected in transit and at rest. Portal documents are never available through public links. We ask that you never email medical records to us; the portal is the only channel we accept documents through, because it is the secure one.</p>
          <p>No system is perfectly secure. If we learn of a breach affecting your personal information, we will notify you as required by applicable law.</p>
        </Section>

        <Section n={4} title="Service Providers We Use">
          <p>We share information with a small number of service providers only as needed to run the Site: our application hosting provider, Google Workspace (email), Calendly (consultation scheduling), and Stripe (payment processing). Each provider processes your information under its own privacy policy and only for the service it performs for us.</p>
        </Section>

        <Section n={5} title="Retention">
          <p>We keep intake information and portal documents while your account is active and for a reasonable period afterward so you can return to your file. You may request deletion of your account and documents at any time (see Your Rights below). Payment records are retained as required for tax and accounting purposes.</p>
        </Section>

        <Section n={6} title="Your Rights and Choices">
          <p>You may:</p>
          <ul className="list-disc pl-6 space-y-2">
            <li>Access, correct, or download the information in your portal at any time by logging in</li>
            <li>Request a copy of the personal information we hold about you</li>
            <li>Request deletion of your account, intake information, and uploaded documents</li>
            <li>Opt out of educational and marketing emails at any time; transactional emails about your account will still be sent</li>
          </ul>
          <p>To exercise any of these rights, email <a href="mailto:intake@veteransdisabilityeducation.com" className="underline underline-offset-4">intake@veteransdisabilityeducation.com</a>. Residents of certain states may have additional rights under state privacy laws; we honor verified requests consistent with applicable law regardless of where you live.</p>
        </Section>

        <Section n={7} title="What We Are Not">
          <p>VDE is a private educational organization. We are not a medical provider, and use of the Site does not create a provider relationship. We are not a HIPAA covered entity; however, we treat the health-related documents you choose to share with the level of care described in this policy. We are not affiliated with or endorsed by the U.S. Department of Veterans Affairs.</p>
        </Section>

        <Section n={8} title="Children">
          <p>The Site is intended for adults. We do not knowingly collect information from anyone under 18.</p>
        </Section>

        <Section n={9} title="Changes to This Policy">
          <p>If we make material changes, we will update the effective date above and post a notice on the Site. Continued use of the Site after changes take effect means you accept the updated policy.</p>
        </Section>

        <Section n={10} title="Contact">
          <div className="bg-secondary p-6 border-l-4 border-accent">
            <div className="font-display text-lg mb-2">Veterans Disability Education</div>
            <div className="text-primary/85">Email: <a href="mailto:intake@veteransdisabilityeducation.com" className="underline underline-offset-4">intake@veteransdisabilityeducation.com</a></div>
            <div className="text-primary/85 mt-1">684 John Wesley Dobbs Ave NE Unit E<br />Atlanta, GA 30312</div>
          </div>
        </Section>

        <div className="mt-14 pt-8 border-t border-border flex items-center justify-between text-sm">
          <Link to="/terms" className="text-primary hover:underline underline-offset-4">Terms of Service →</Link>
          <Link to="/" className="text-primary hover:underline underline-offset-4">Back to home</Link>
        </div>
      </article>
    </div>
  );
}
